/**
 * MessageComposer Component
 *
 * Input area for composing and sending messages.
 * Supports text input, send button, character count, and quick reply templates.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-476 - Core Messaging Infrastructure (13 SP)
 * Location: apps/web/src/components/messages/MessageComposer.jsx
 */

import React, { useState, useRef, useEffect } from 'react';

const MAX_LENGTH = 5000;

/**
 * MessageComposer Component
 *
 * @param {Object} props
 * @param {Function} props.onSendMessage - Callback when message is sent (text) => void
 * @param {boolean} props.disabled - Disable input
 * @param {boolean} props.sending - Sending state
 * @param {Function} props.onOpenTemplates - Callback to open quick reply templates
 * @param {string} props.injectedText - Template text to populate the composer with
 * @param {number} props.injectionKey - Changes each time a template is selected, so
 *   re-selecting the same template re-injects it
 */
const MessageComposer = ({
  onSendMessage,
  disabled = false,
  sending = false,
  onOpenTemplates = null,
  injectedText = '',
  injectionKey = null,
}) => {
  const [text, setText] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const textareaRef = useRef(null);

  // Populate composer when a template is selected. Keyed on injectionKey so the
  // same template can be injected again.
  useEffect(() => {
    if (injectionKey && injectedText) {
      setText(injectedText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injectionKey]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [text]);

  const handleSubmit = (  _e) => {
    _e.preventDefault();

    const trimmedText = text.trim();
    if (!trimmedText || disabled || sending) {return;}

    if (trimmedText.length > MAX_LENGTH) {
      setErrorMessage(`Message cannot exceed ${MAX_LENGTH} characters`);
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }

    onSendMessage(trimmedText);
    setText('');
  };

  const handleKeyDown = (  _e) => {
    // Send on Enter (without Shift)
    if (_e.key === 'Enter' && !_e.shiftKey) {
      _e.preventDefault();
      handleSubmit(_e);
    }
  };

  const remainingChars = MAX_LENGTH - text.length;
  const showCharCount = text.length > MAX_LENGTH * 0.8; // Show when 80% full

  return (
    <form onSubmit={handleSubmit} className="border-t border-gray-200 bg-white p-4">
      <div className="flex items-end space-x-2">
        {/* Quick Reply Templates Button */}
        {onOpenTemplates && (
          <button
            type="button"
            onClick={onOpenTemplates}
            disabled={disabled || sending}
            className="flex-shrink-0 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Quick replies"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h7"
              />
            </svg>
          </button>
        )}

        {/* Text Input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled || sending}
            placeholder="Type a message..."
            rows={1}
            maxLength={MAX_LENGTH}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
            style={{ maxHeight: '150px' }}
          />

          {/* Character Count */}
          {showCharCount && (
            <div
              className={`absolute bottom-2 right-2 text-xs font-medium ${
                remainingChars < 0
                  ? 'text-red-600'
                  : remainingChars < 500
                  ? 'text-yellow-600'
                  : 'text-gray-500'
              }`}
            >
              {remainingChars < 0 ? `${Math.abs(remainingChars)} over` : remainingChars}
            </div>
          )}
        </div>

        {/* Send Button */}
        <button
          type="submit"
          disabled={!text.trim() || disabled || sending || remainingChars < 0}
          className="flex-shrink-0 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {sending ? (
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Hint Text */}
      <p className="mt-2 text-xs text-gray-500">
        Press <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">Enter</kbd> to send,{' '}
        <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">Shift + Enter</kbd> for new line
      </p>

      {/* Error Message */}
      {errorMessage && (
        <div className="mt-2 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
          {errorMessage}
        </div>
      )}
    </form>
  );
};

export default MessageComposer;
